import React, { useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { ChevronDown, ChevronUp, Settings2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ORDER_COLUMNS } from './orderColumns';
import { useTrans } from '../../hooks/useTrans';

function MoveButton({ disabled, label, onClick, children }) {
    return (
        <button
            type="button"
            className="p-1 text-[#003545] hover:bg-[#ededed] rounded-sm cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
            onClick={onClick}
            disabled={disabled}
            title={label}
        >
            {children}
        </button>
    );
}

function ColumnRow({ column, checked, disabled = false, onToggle, children }) {
    const translate = useTrans();

    return (
        <li className="flex items-center gap-2 py-1">
            <Checkbox id={`column-${column.key}`} checked={checked} disabled={disabled} onCheckedChange={onToggle} />
            <label htmlFor={`column-${column.key}`} className="grow text-sm text-[#003545] cursor-pointer">
                {translate(column.label)}
            </label>
            {children}
        </li>
    );
}

export function OrderColumnSettings({ visibleColumns, onChange }) {
    const translate = useTrans();
    const [open, setOpen] = useState(false);
    const changeVersion = useRef(0);
    const savedVersion = useRef(0);

    const visible = visibleColumns
        .map((key) => ORDER_COLUMNS.find((column) => column.key === key))
        .filter(Boolean);
    const hidden = ORDER_COLUMNS.filter((column) => !visibleColumns.includes(column.key));

    function apply(columns) {
        changeVersion.current += 1;
        onChange(columns);
    }

    function handleOpenChange(nextOpen) {
        setOpen(nextOpen);

        if (nextOpen || changeVersion.current === savedVersion.current) {
            return;
        }

        const postedVersion = changeVersion.current;

        router.post('/orders/kolommen', { columns: visibleColumns }, {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
                savedVersion.current = postedVersion;
            },
        });
    }

    function toggle(key) {
        if (! visibleColumns.includes(key)) {
            apply([...visibleColumns, key]);

            return;
        }

        apply(visibleColumns.filter((columnKey) => columnKey !== key));
    }

    function move(index, offset) {
        const target = index + offset;

        if (target < 0 || target >= visibleColumns.length) {
            return;
        }

        const reordered = [...visibleColumns];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

        apply(reordered);
    }

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="font-normal rounded-sm border-[#bbb] shadow-none">
                    <Settings2 className="size-4 mr-2" />
                    {translate('Kolommen')}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-2">
                <ul className="flex flex-col">
                    {visible.map((column, index) => (
                        <ColumnRow key={column.key} column={column} checked disabled={visible.length === 1} onToggle={() => toggle(column.key)}>
                            <MoveButton disabled={index === 0} label={translate('Omhoog')} onClick={() => move(index, -1)}>
                                <ChevronUp className="size-4" />
                            </MoveButton>
                            <MoveButton disabled={index === visible.length - 1} label={translate('Omlaag')} onClick={() => move(index, 1)}>
                                <ChevronDown className="size-4" />
                            </MoveButton>
                        </ColumnRow>
                    ))}
                </ul>

                {hidden.length > 0 && (
                    <>
                        <p className="mt-3 mb-1 text-xs uppercase tracking-wide text-gray-500">{translate('Verborgen kolommen')}</p>
                        <ul className="flex flex-col">
                            {hidden.map((column) => (
                                <ColumnRow key={column.key} column={column} checked={false} onToggle={() => toggle(column.key)} />
                            ))}
                        </ul>
                    </>
                )}

                <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-3 font-normal rounded-sm border-[#bbb] shadow-none"
                    onClick={() => apply(ORDER_COLUMNS.map((column) => column.key))}
                >
                    {translate('Alle kolommen tonen')}
                </Button>
            </PopoverContent>
        </Popover>
    );
}
